<template>
    <PageHeader :title="$t('setup_create.title')" @back="goBack" />

    <div class="flex-1 space-y-6 overflow-y-auto p-4">
        <div v-if="mode === 'choice' && !hasStartedCustomizing" class="space-y-6">
            <div>
                <h3 class="mb-4 text-xs font-bold text-gray-400 uppercase">
                    {{ $t('setup_create.bought_dart_heading') }}
                </h3>
                <button
                    @click="selectPredefinedDart"
                    class="border-stroke-1 bg-bg hover:bg-card flex w-full items-center justify-between border p-4 text-left"
                >
                    <div class="flex-1">
                        <div class="mb-1 text-base font-bold text-white">{{ $t('setup_create.dart_button') }}</div>
                        <div class="text-sm text-gray-400">
                            {{ $t('setup_create.dart_button_desc') }}
                        </div>
                    </div>
                    <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        class="text-gray-400"
                    >
                        <path d="M9 18l6-6-6-6" />
                    </svg>
                </button>
            </div>

            <div class="flex justify-center">
                <span class="text-sm font-bold text-red-600 uppercase">{{ $t('setup_create.or') }}</span>
            </div>

            <div v-if="!selectedProducts.dart">
                <h3 class="mb-4 text-xs font-bold text-gray-400 uppercase">{{ $t('setup_create.new_dart_heading') }}</h3>
                <div class="border-stroke-1 space-y-0 border">
                    <div v-if="selectedProducts.shaft" class="border-stroke-1 border-b">
                        <div class="bg-bg flex w-full items-center gap-4 p-4">
                            <div
                                v-if="selectedProducts.shaft.image_url"
                                class="h-16 w-16 shrink-0 overflow-hidden rounded"
                            >
                                <img
                                    :src="getImageUrl(selectedProducts.shaft.image_url)"
                                    :alt="selectedProducts.shaft.name"
                                    class="h-full w-full bg-white object-contain p-1"
                                />
                            </div>
                            <div
                                v-else
                                class="flex h-16 w-16 shrink-0 items-center justify-center rounded bg-zinc-700"
                            >
                                <svg
                                    class="h-8 w-8 text-gray-400"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        stroke-width="2"
                                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                    />
                                </svg>
                            </div>
                            <div class="min-w-0 flex-1">
                                <div class="mb-1 text-base font-bold text-white">{{ $t('setup_create.shaft_label') }}</div>
                                <div class="truncate text-sm text-white">
                                    {{ selectedProducts.shaft.name }}
                                </div>
                            </div>
                            <button
                                @click.stop="removeComponent('shaft')"
                                class="shrink-0 text-red-600 hover:text-red-500"
                                :aria-label="$t('setup_create.remove_shaft_aria')"
                            >
                                <svg
                                    width="20"
                                    height="20"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="2"
                                >
                                    <path
                                        d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"
                                    />
                                </svg>
                            </button>
                        </div>
                        <div class="bg-bg border-stroke-1 border-t px-4 pt-4 pb-4">
                            <input
                                :value="formData.shaft_comment"
                                @input="
                                    dartSetupStore.setComponentComment('shaft', $event.target.value)
                                "
                                type="text"
                                maxlength="255"
                                :placeholder="$t('setup_create.comment_placeholder')"
                                class="bg-card w-full border border-zinc-700 px-3 py-2 text-sm text-white transition-colors focus:border-red-500 focus:outline-none"
                            />
                        </div>
                    </div>
                    <button
                        v-else
                        @click="selectComponent('shaft')"
                        class="border-stroke-1 bg-bg hover:bg-card flex w-full items-center justify-between border-b p-4 text-left last:border-b-0"
                    >
                        <div class="flex-1">
                            <div class="mb-1 text-base font-bold text-white">{{ $t('setup_create.shaft_label') }}</div>
                            <div class="text-sm text-gray-400">{{ $t('setup_create.choose_shaft') }}</div>
                        </div>
                        <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            class="text-gray-400"
                        >
                            <path d="M9 18l6-6-6-6" />
                        </svg>
                    </button>

                    <div v-if="selectedProducts.barrel" class="border-stroke-1 border-b">
                        <div class="bg-bg flex w-full items-center gap-4 p-4">
                            <div
                                v-if="selectedProducts.barrel.image_url"
                                class="h-16 w-16 shrink-0 overflow-hidden rounded"
                            >
                                <img
                                    :src="getImageUrl(selectedProducts.barrel.image_url)"
                                    :alt="selectedProducts.barrel.name"
                                    class="h-full w-full bg-white object-contain p-1"
                                />
                            </div>
                            <div
                                v-else
                                class="flex h-16 w-16 shrink-0 items-center justify-center rounded bg-zinc-700"
                            >
                                <svg
                                    class="h-8 w-8 text-gray-400"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        stroke-width="2"
                                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                    />
                                </svg>
                            </div>
                            <div class="min-w-0 flex-1">
                                <div class="mb-1 text-base font-bold text-white">{{ $t('setup_create.barrel_label') }}</div>
                                <div class="truncate text-sm text-white">
                                    {{ selectedProducts.barrel.name }}
                                </div>
                            </div>
                            <button
                                @click.stop="removeComponent('barrel')"
                                class="shrink-0 text-red-600 hover:text-red-500"
                                :aria-label="$t('setup_create.remove_barrel_aria')"
                            >
                                <svg
                                    width="20"
                                    height="20"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="2"
                                >
                                    <path
                                        d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"
                                    />
                                </svg>
                            </button>
                        </div>
                        <div class="bg-bg border-stroke-1 border-t px-4 pt-4 pb-4">
                            <input
                                :value="formData.barrel_comment"
                                @input="
                                    dartSetupStore.setComponentComment(
                                        'barrel',
                                        $event.target.value,
                                    )
                                "
                                type="text"
                                maxlength="255"
                                :placeholder="$t('setup_create.comment_placeholder')"
                                class="bg-card w-full border border-zinc-700 px-3 py-2 text-sm text-white transition-colors focus:border-red-500 focus:outline-none"
                            />
                        </div>
                    </div>
                    <button
                        v-else
                        @click="selectComponent('barrel')"
                        data-testid="setup-component-barrel"
                        class="border-stroke-1 bg-bg hover:bg-card flex w-full items-center justify-between border-b p-4 text-left last:border-b-0"
                    >
                        <div class="flex-1">
                            <div class="mb-1 text-base font-bold text-white">{{ $t('setup_create.barrel_label') }}</div>
                            <div class="text-sm text-gray-400">{{ $t('setup_create.choose_barrel') }}</div>
                        </div>
                        <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            class="text-gray-400"
                        >
                            <path d="M9 18l6-6-6-6" />
                        </svg>
                    </button>

                    <div v-if="selectedProducts.flight" class="border-stroke-1 border-b">
                        <div class="bg-bg flex w-full items-center gap-4 p-4">
                            <div
                                v-if="selectedProducts.flight.image_url"
                                class="h-16 w-16 shrink-0 overflow-hidden rounded"
                            >
                                <img
                                    :src="getImageUrl(selectedProducts.flight.image_url)"
                                    :alt="selectedProducts.flight.name"
                                    class="h-full w-full bg-white object-contain p-1"
                                />
                            </div>
                            <div
                                v-else
                                class="flex h-16 w-16 shrink-0 items-center justify-center rounded bg-zinc-700"
                            >
                                <svg
                                    class="h-8 w-8 text-gray-400"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        stroke-width="2"
                                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                    />
                                </svg>
                            </div>
                            <div class="min-w-0 flex-1">
                                <div class="mb-1 text-base font-bold text-white">{{ $t('setup_create.flight_label') }}</div>
                                <div class="truncate text-sm text-white">
                                    {{ selectedProducts.flight.name }}
                                </div>
                            </div>
                            <button
                                @click.stop="removeComponent('flight')"
                                class="shrink-0 text-red-600 hover:text-red-500"
                                :aria-label="$t('setup_create.remove_flight_aria')"
                            >
                                <svg
                                    width="20"
                                    height="20"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="2"
                                >
                                    <path
                                        d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"
                                    />
                                </svg>
                            </button>
                        </div>
                        <div class="bg-bg border-stroke-1 border-t px-4 pt-4 pb-4">
                            <input
                                :value="formData.flight_comment"
                                @input="
                                    dartSetupStore.setComponentComment(
                                        'flight',
                                        $event.target.value,
                                    )
                                "
                                type="text"
                                maxlength="255"
                                :placeholder="$t('setup_create.comment_placeholder')"
                                class="bg-card w-full border border-zinc-700 px-3 py-2 text-sm text-white transition-colors focus:border-red-500 focus:outline-none"
                            />
                        </div>
                    </div>
                    <button
                        v-else
                        @click="selectComponent('flight')"
                        class="border-stroke-1 bg-bg hover:bg-card flex w-full items-center justify-between border-b p-4 text-left last:border-b-0"
                    >
                        <div class="flex-1">
                            <div class="mb-1 text-base font-bold text-white">{{ $t('setup_create.flight_label') }}</div>
                            <div class="text-sm text-gray-400">{{ $t('setup_create.choose_flight') }}</div>
                        </div>
                        <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            class="text-gray-400"
                        >
                            <path d="M9 18l6-6-6-6" />
                        </svg>
                    </button>

                    <div v-if="selectedProducts.tip" class="border-stroke-1">
                        <div class="bg-bg flex w-full items-center gap-4 p-4">
                            <div
                                v-if="selectedProducts.tip.image_url"
                                class="h-16 w-16 shrink-0 overflow-hidden rounded"
                            >
                                <img
                                    :src="getImageUrl(selectedProducts.tip.image_url)"
                                    :alt="selectedProducts.tip.name"
                                    class="h-full w-full bg-white object-contain p-1"
                                />
                            </div>
                            <div
                                v-else
                                class="flex h-16 w-16 shrink-0 items-center justify-center rounded bg-zinc-700"
                            >
                                <svg
                                    class="h-8 w-8 text-gray-400"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        stroke-width="2"
                                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                    />
                                </svg>
                            </div>
                            <div class="min-w-0 flex-1">
                                <div class="mb-1 text-base font-bold text-white">{{ $t('setup_create.point_label') }}</div>
                                <div class="truncate text-sm text-white">
                                    {{ selectedProducts.tip.name }}
                                </div>
                            </div>
                            <button
                                @click.stop="removeComponent('tip')"
                                class="shrink-0 text-red-600 hover:text-red-500"
                                :aria-label="$t('setup_create.remove_point_aria')"
                            >
                                <svg
                                    width="20"
                                    height="20"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="2"
                                >
                                    <path
                                        d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"
                                    />
                                </svg>
                            </button>
                        </div>
                        <div class="bg-bg border-stroke-1 border-t px-4 pt-4 pb-4">
                            <input
                                :value="formData.tip_comment"
                                @input="
                                    dartSetupStore.setComponentComment('tip', $event.target.value)
                                "
                                type="text"
                                maxlength="255"
                                :placeholder="$t('setup_create.comment_placeholder')"
                                class="bg-card w-full border border-zinc-700 px-3 py-2 text-sm text-white transition-colors focus:border-red-500 focus:outline-none"
                            />
                        </div>
                    </div>
                    <button
                        v-else
                        @click="selectComponent('tip')"
                        class="border-stroke-1 bg-bg hover:bg-card flex w-full items-center justify-between p-4 text-left"
                    >
                        <div class="flex-1">
                            <div class="mb-1 text-base font-bold text-white">{{ $t('setup_create.point_label') }}</div>
                            <div class="text-sm text-gray-400">{{ $t('setup_create.choose_point') }}</div>
                        </div>
                        <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            class="text-gray-400"
                        >
                            <path d="M9 18l6-6-6-6" />
                        </svg>
                    </button>
                </div>
            </div>
        </div>

        <div v-if="hasStartedCustomizing" class="space-y-6">
            <div v-if="selectedProducts.dart">
                <h3 class="mb-4 text-xs font-bold text-gray-400 uppercase">{{ $t('setup_create.your_dart_heading') }}</h3>
                <div class="border-stroke-1 space-y-0 border">
                    <div class="border-stroke-1">
                        <div class="bg-bg flex w-full items-center gap-4 p-4">
                            <div
                                v-if="selectedProducts.dart.image_url"
                                class="h-16 w-16 shrink-0 overflow-hidden rounded"
                            >
                                <img
                                    :src="getImageUrl(selectedProducts.dart.image_url)"
                                    :alt="selectedProducts.dart.name"
                                    class="h-full w-full bg-white object-contain p-1"
                                />
                            </div>
                            <div
                                v-else
                                class="flex h-16 w-16 shrink-0 items-center justify-center rounded bg-zinc-700"
                            >
                                <svg
                                    class="h-8 w-8 text-gray-400"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        stroke-width="2"
                                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                    />
                                </svg>
                            </div>
                            <div class="min-w-0 flex-1">
                                <div class="mb-1 text-base font-bold text-white">{{ $t('setup_create.dart_button') }}</div>
                                <div class="truncate text-sm text-white">
                                    {{ selectedProducts.dart.name }}
                                </div>
                            </div>
                            <button
                                @click.stop="removeComponent('dart')"
                                class="shrink-0 text-red-600 hover:text-red-500"
                                :aria-label="$t('setup_create.remove_dart_aria')"
                            >
                                <svg
                                    width="20"
                                    height="20"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="2"
                                >
                                    <path
                                        d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"
                                    />
                                </svg>
                            </button>
                        </div>
                        <div class="bg-bg border-stroke-1 border-t px-4 pt-4 pb-4">
                            <input
                                :value="formData.dart_comment"
                                @input="
                                    dartSetupStore.setComponentComment('dart', $event.target.value)
                                "
                                type="text"
                                maxlength="255"
                                :placeholder="$t('setup_create.comment_placeholder')"
                                class="bg-card w-full border border-zinc-700 px-3 py-2 text-sm text-white transition-colors focus:border-red-500 focus:outline-none"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div v-else>
                <h3 class="mb-4 text-xs font-bold text-gray-400 uppercase">{{ $t('setup_create.new_dart_heading') }}</h3>
                <div class="border-stroke-1 space-y-0 border">
                    <div v-if="selectedProducts.shaft" class="border-stroke-1 border-b">
                        <div class="bg-bg flex w-full items-center gap-4 p-4">
                            <div
                                v-if="selectedProducts.shaft.image_url"
                                class="h-16 w-16 shrink-0 overflow-hidden rounded"
                            >
                                <img
                                    :src="getImageUrl(selectedProducts.shaft.image_url)"
                                    :alt="selectedProducts.shaft.name"
                                    class="h-full w-full bg-white object-contain p-1"
                                />
                            </div>
                            <div
                                v-else
                                class="flex h-16 w-16 shrink-0 items-center justify-center rounded bg-zinc-700"
                            >
                                <svg
                                    class="h-8 w-8 text-gray-400"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        stroke-width="2"
                                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                    />
                                </svg>
                            </div>
                            <div class="min-w-0 flex-1">
                                <div class="mb-1 text-base font-bold text-white">{{ $t('setup_create.shaft_label') }}</div>
                                <div class="truncate text-sm text-white">
                                    {{ selectedProducts.shaft.name }}
                                </div>
                            </div>
                            <button
                                @click.stop="removeComponent('shaft')"
                                class="shrink-0 text-red-600 hover:text-red-500"
                                :aria-label="$t('setup_create.remove_shaft_aria')"
                            >
                                <svg
                                    width="20"
                                    height="20"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="2"
                                >
                                    <path
                                        d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"
                                    />
                                </svg>
                            </button>
                        </div>
                        <div class="bg-bg border-stroke-1 border-t px-4 pt-4 pb-4">
                            <input
                                :value="formData.shaft_comment"
                                @input="
                                    dartSetupStore.setComponentComment('shaft', $event.target.value)
                                "
                                type="text"
                                maxlength="255"
                                :placeholder="$t('setup_create.comment_placeholder')"
                                class="bg-card w-full border border-zinc-700 px-3 py-2 text-sm text-white transition-colors focus:border-red-500 focus:outline-none"
                            />
                        </div>
                    </div>
                    <button
                        v-else
                        @click="selectComponent('shaft')"
                        class="border-stroke-1 bg-bg hover:bg-card flex w-full items-center justify-between border-b p-4 text-left last:border-b-0"
                    >
                        <div class="flex-1">
                            <div class="mb-1 text-base font-bold text-white">{{ $t('setup_create.shaft_label') }}</div>
                            <div class="text-sm text-gray-400">{{ $t('setup_create.choose_shaft') }}</div>
                        </div>
                        <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            class="text-gray-400"
                        >
                            <path d="M9 18l6-6-6-6" />
                        </svg>
                    </button>

                    <div v-if="selectedProducts.barrel" class="border-stroke-1 border-b">
                        <div class="bg-bg flex w-full items-center gap-4 p-4">
                            <div
                                v-if="selectedProducts.barrel.image_url"
                                class="h-16 w-16 shrink-0 overflow-hidden rounded"
                            >
                                <img
                                    :src="getImageUrl(selectedProducts.barrel.image_url)"
                                    :alt="selectedProducts.barrel.name"
                                    class="h-full w-full bg-white object-contain p-1"
                                />
                            </div>
                            <div
                                v-else
                                class="flex h-16 w-16 shrink-0 items-center justify-center rounded bg-zinc-700"
                            >
                                <svg
                                    class="h-8 w-8 text-gray-400"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        stroke-width="2"
                                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                    />
                                </svg>
                            </div>
                            <div class="min-w-0 flex-1">
                                <div class="mb-1 text-base font-bold text-white">{{ $t('setup_create.barrel_label') }}</div>
                                <div class="truncate text-sm text-white">
                                    {{ selectedProducts.barrel.name }}
                                </div>
                            </div>
                            <button
                                @click.stop="removeComponent('barrel')"
                                class="shrink-0 text-red-600 hover:text-red-500"
                                :aria-label="$t('setup_create.remove_barrel_aria')"
                            >
                                <svg
                                    width="20"
                                    height="20"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="2"
                                >
                                    <path
                                        d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"
                                    />
                                </svg>
                            </button>
                        </div>
                        <div class="bg-bg border-stroke-1 border-t px-4 pt-4 pb-4">
                            <input
                                :value="formData.barrel_comment"
                                @input="
                                    dartSetupStore.setComponentComment(
                                        'barrel',
                                        $event.target.value,
                                    )
                                "
                                type="text"
                                maxlength="255"
                                :placeholder="$t('setup_create.comment_placeholder')"
                                class="bg-card w-full border border-zinc-700 px-3 py-2 text-sm text-white transition-colors focus:border-red-500 focus:outline-none"
                            />
                        </div>
                    </div>
                    <button
                        v-else
                        @click="selectComponent('barrel')"
                        data-testid="setup-component-barrel"
                        class="border-stroke-1 bg-bg hover:bg-card flex w-full items-center justify-between border-b p-4 text-left last:border-b-0"
                    >
                        <div class="flex-1">
                            <div class="mb-1 text-base font-bold text-white">{{ $t('setup_create.barrel_label') }}</div>
                            <div class="text-sm text-gray-400">{{ $t('setup_create.choose_barrel') }}</div>
                        </div>
                        <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            class="text-gray-400"
                        >
                            <path d="M9 18l6-6-6-6" />
                        </svg>
                    </button>

                    <div v-if="selectedProducts.flight" class="border-stroke-1 border-b">
                        <div class="bg-bg flex w-full items-center gap-4 p-4">
                            <div
                                v-if="selectedProducts.flight.image_url"
                                class="h-16 w-16 shrink-0 overflow-hidden rounded"
                            >
                                <img
                                    :src="getImageUrl(selectedProducts.flight.image_url)"
                                    :alt="selectedProducts.flight.name"
                                    class="h-full w-full bg-white object-contain p-1"
                                />
                            </div>
                            <div
                                v-else
                                class="flex h-16 w-16 shrink-0 items-center justify-center rounded bg-zinc-700"
                            >
                                <svg
                                    class="h-8 w-8 text-gray-400"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        stroke-width="2"
                                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                    />
                                </svg>
                            </div>
                            <div class="min-w-0 flex-1">
                                <div class="mb-1 text-base font-bold text-white">{{ $t('setup_create.flight_label') }}</div>
                                <div class="truncate text-sm text-white">
                                    {{ selectedProducts.flight.name }}
                                </div>
                            </div>
                            <button
                                @click.stop="removeComponent('flight')"
                                class="shrink-0 text-red-600 hover:text-red-500"
                                :aria-label="$t('setup_create.remove_flight_aria')"
                            >
                                <svg
                                    width="20"
                                    height="20"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="2"
                                >
                                    <path
                                        d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"
                                    />
                                </svg>
                            </button>
                        </div>
                        <div class="bg-bg border-stroke-1 border-t px-4 pt-4 pb-4">
                            <input
                                :value="formData.flight_comment"
                                @input="
                                    dartSetupStore.setComponentComment(
                                        'flight',
                                        $event.target.value,
                                    )
                                "
                                type="text"
                                maxlength="255"
                                :placeholder="$t('setup_create.comment_placeholder')"
                                class="bg-card w-full border border-zinc-700 px-3 py-2 text-sm text-white transition-colors focus:border-red-500 focus:outline-none"
                            />
                        </div>
                    </div>
                    <button
                        v-else
                        @click="selectComponent('flight')"
                        class="border-stroke-1 bg-bg hover:bg-card flex w-full items-center justify-between border-b p-4 text-left last:border-b-0"
                    >
                        <div class="flex-1">
                            <div class="mb-1 text-base font-bold text-white">{{ $t('setup_create.flight_label') }}</div>
                            <div class="text-sm text-gray-400">{{ $t('setup_create.choose_flight') }}</div>
                        </div>
                        <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            class="text-gray-400"
                        >
                            <path d="M9 18l6-6-6-6" />
                        </svg>
                    </button>

                    <div v-if="selectedProducts.tip" class="border-stroke-1">
                        <div class="bg-bg flex w-full items-center gap-4 p-4">
                            <div
                                v-if="selectedProducts.tip.image_url"
                                class="h-16 w-16 shrink-0 overflow-hidden rounded"
                            >
                                <img
                                    :src="getImageUrl(selectedProducts.tip.image_url)"
                                    :alt="selectedProducts.tip.name"
                                    class="h-full w-full bg-white object-contain p-1"
                                />
                            </div>
                            <div
                                v-else
                                class="flex h-16 w-16 shrink-0 items-center justify-center rounded bg-zinc-700"
                            >
                                <svg
                                    class="h-8 w-8 text-gray-400"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        stroke-width="2"
                                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                    />
                                </svg>
                            </div>
                            <div class="min-w-0 flex-1">
                                <div class="mb-1 text-base font-bold text-white">{{ $t('setup_create.point_label') }}</div>
                                <div class="truncate text-sm text-white">
                                    {{ selectedProducts.tip.name }}
                                </div>
                            </div>
                            <button
                                @click.stop="removeComponent('tip')"
                                class="shrink-0 text-red-600 hover:text-red-500"
                                :aria-label="$t('setup_create.remove_point_aria')"
                            >
                                <svg
                                    width="20"
                                    height="20"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="2"
                                >
                                    <path
                                        d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"
                                    />
                                </svg>
                            </button>
                        </div>
                        <div class="bg-bg border-stroke-1 border-t px-4 pt-4 pb-4">
                            <input
                                :value="formData.tip_comment"
                                @input="
                                    dartSetupStore.setComponentComment('tip', $event.target.value)
                                "
                                type="text"
                                maxlength="255"
                                :placeholder="$t('setup_create.comment_placeholder')"
                                class="bg-card w-full border border-zinc-700 px-3 py-2 text-sm text-white transition-colors focus:border-red-500 focus:outline-none"
                            />
                        </div>
                    </div>
                    <button
                        v-else
                        @click="selectComponent('tip')"
                        class="border-stroke-1 bg-bg hover:bg-card flex w-full items-center justify-between p-4 text-left"
                    >
                        <div class="flex-1">
                            <div class="mb-1 text-base font-bold text-white">{{ $t('setup_create.point_label') }}</div>
                            <div class="text-sm text-gray-400">{{ $t('setup_create.choose_point') }}</div>
                        </div>
                        <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            class="text-gray-400"
                        >
                            <path d="M9 18l6-6-6-6" />
                        </svg>
                    </button>
                </div>
            </div>

            <div
                v-if="errorMessage"
                class="border border-red-600 bg-red-900/50 p-4"
                :style="{ clipPath: 'polygon(8px 0, 100% 0, 100% 100%, 0 100%, 0 8px)' }"
            >
                <div class="text-sm text-red-300">{{ errorMessage }}</div>
            </div>
        </div>
    </div>

    <div
        v-if="hasStartedCustomizing"
        class="bg-card border-stroke-1 sticky bottom-0 z-10 border-t p-4"
        :style="{ paddingBottom: 'calc(1rem + var(--safezone-bottom, 0px))' }"
    >
        <div class="mb-4 flex items-center justify-between">
            <h4 class="text-md font-bold uppercase">{{ $t('setup_edit.default_toggle_label') }}</h4>
            <Toggle v-model="isDefaultSetup" class="cursor-pointer" />
        </div>
        <p class="mb-4 font-sans text-sm leading-normal font-normal tracking-tight">
            {{ $t('setup_edit.default_toggle_description') }}
        </p>
        <DiagonalCutButton
            type="button"
            :disabled="isSaving || !isFormValid"
            variant="primary"
            cut-position="right-top"
            :full-width="true"
            data-testid="setup-create-button"
            @click="openNameModal"
        >
            {{ $t('setup_create.create_button') }}
        </DiagonalCutButton>
    </div>

    <div
        v-if="showNameModal"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/65"
        @click.self="closeNameModal"
    >
        <div
            class="bg-bg border-stroke-1 mx-4 w-full max-w-md border shadow-xl"
            :style="{ clipPath: 'polygon(8px 0, 100% 0, 100% 100%, 0 100%, 0 8px)' }"
        >
            <div class="p-4">
                <h2
                    class="font-title mb-2 text-center text-base font-bold tracking-wide text-white uppercase"
                >
                    {{ $t('setup_create.name_modal_title') }}
                </h2>
                <p class="mb-4 text-center text-sm text-gray-400">
                    {{ $t('setup_create.name_modal_subtitle') }}
                </p>
                <input
                    ref="nameModalInput"
                    v-model="modalNameValue"
                    type="text"
                    maxlength="50"
                    :placeholder="$t('setup_create.setup_name_placeholder')"
                    data-testid="setup-name-input"
                    class="bg-card w-full border border-zinc-700 px-4 py-3 text-white transition-colors focus:border-red-500 focus:outline-none"
                    :class="{ 'border-red-600': modalNameError }"
                    @keydown.enter="confirmCreateSetup"
                />
                <p class="mt-1 text-xs text-gray-400">{{ modalNameValue.length }}/50</p>
                <p v-if="modalNameError" class="mt-2 text-sm text-red-400">{{ modalNameError }}</p>
            </div>
            <div class="border-stroke-1 grid grid-cols-2 gap-4 border-t p-4">
                <DiagonalCutButton
                    variant="secondary"
                    cut-position="right-top"
                    :disabled="isSaving"
                    @click="closeNameModal"
                >
                    {{ $t('general.cancel') }}
                </DiagonalCutButton>
                <DiagonalCutButton
                    variant="primary"
                    cut-position="left-top"
                    :disabled="isSaving"
                    :loading="isSaving"
                    data-testid="setup-name-confirm"
                    @click="confirmCreateSetup"
                >
                    {{ $t('setup_create.create_button') }}
                </DiagonalCutButton>
            </div>
        </div>
    </div>

    <Modal
        :is-visible="showUnsavedChangesModal"
        :on-confirm="confirmLeaveWithoutSaving"
        :on-decline="() => (showUnsavedChangesModal = false)"
    >
        <p>{{ $t('setup_create.unsaved_changes_confirm') }}</p>
    </Modal>
</template>

<script>
import { mapStores } from 'pinia'
import { useAuthStore } from '@/stores/auth.js'
import { useDartSetupStore } from '@/stores/dartSetup.js'
import { useMatchInviteStore } from '@/stores/matchInvite'
import PageHeader from '@/components/PageHeader.vue'
import DiagonalCutButton from '@/components/ui/DiagonalCutButton.vue'
import Toggle from '@/components/ui/Form/Toggle.vue'
import Modal from '@/components/Modal.vue'

export default {
    name: 'SetupCreateView',
    components: {
        PageHeader,
        DiagonalCutButton,
        Toggle,
        Modal,
    },
    computed: {
        ...mapStores(useAuthStore, useDartSetupStore, useMatchInviteStore),
        mode() {
            return this.$route.query.mode || 'choice'
        },
        formData: {
            get() {
                return this.dartSetupStore.formData
            },
            set(value) {
                this.dartSetupStore.setFormData(value)
            },
        },
        selectedProducts() {
            return this.dartSetupStore.selectedProducts
        },
        isDefaultSetup: {
            get() {
                return this.dartSetupStore.isDefaultSetup
            },
            set(value) {
                this.dartSetupStore.setIsDefaultSetup(value)
            },
        },
        hasStartedCustomizing() {
            return this.dartSetupStore.hasStartedCustomizing
        },
        isCustomizeComplete() {
            return this.dartSetupStore.isCustomizeComplete
        },
        isFormValid() {
            return this.dartSetupStore.isFormValid
        },
    },
    data() {
        return {
            isSaving: false,
            errorMessage: '',
            showNameModal: false,
            modalNameValue: '',
            modalNameError: '',
            showUnsavedChangesModal: false,
        }
    },
    watch: {
        showNameModal(val) {
            if (val) {
                this.modalNameValue = this.formData.name || ''
                this.modalNameError = ''
                this.$nextTick(() => this.$refs.nameModalInput?.focus())
            }
        },
        '$route.query': {
            immediate: true,
            handler(newQuery) {
                if (newQuery.component && newQuery.productId) {
                    this.dartSetupStore.setComponent(
                        newQuery.component,
                        parseInt(newQuery.productId),
                    )
                }
            },
        },
    },
    methods: {
        selectPredefinedDart() {
            this.$router.replace({
                name: 'setup-select-product',
                query: { component: 'dart' },
            })
        },
        openNameModal() {
            if (this.isSaving || !this.isFormValid) return
            this.showNameModal = true
        },
        closeNameModal() {
            if (this.isSaving) return
            this.showNameModal = false
        },
        confirmCreateSetup() {
            const name = this.modalNameValue?.trim()
            if (!name) {
                this.modalNameError = this.$t('setup_create.name_required_error')
                return
            }
            this.modalNameError = ''
            this.dartSetupStore.setName(name)
            this.handleSubmit()
        },
        selectComponent(component) {
            this.$router.replace({
                name: 'setup-select-product',
                query: { component },
            })
        },
        async handleSubmit() {
            if (this.isSaving || !this.isFormValid) return
            const name = this.formData.name?.trim()
            if (!name) return

            this.isSaving = true
            this.errorMessage = ''

            try {
                const setupData = {
                    name: this.formData.name.trim(),
                    dart_id: this.formData.dart_id,
                    tip_id: this.formData.tip_id,
                    barrel_id: this.formData.barrel_id,
                    shaft_id: this.formData.shaft_id,
                    flight_id: this.formData.flight_id,
                    dart_comment: this.formData.dart_comment?.trim() || null,
                    tip_comment: this.formData.tip_comment?.trim() || null,
                    barrel_comment: this.formData.barrel_comment?.trim() || null,
                    shaft_comment: this.formData.shaft_comment?.trim() || null,
                    flight_comment: this.formData.flight_comment?.trim() || null,
                    is_default: this.isDefaultSetup,
                }

                const response = await this.authStore.createSetup(setupData)

                this.showNameModal = false
                this.dartSetupStore.clearDraft()
                this.$toast(this.$t('setup_create.create_success'), 3000)

                const entryPoint = this.dartSetupStore.entryPoint
                const isMatchReturn =
                    entryPoint?.type === 'route' &&
                    ['match-setup', 'match-lobby'].includes(entryPoint.name)

                this.returnToEntryPoint(
                    isMatchReturn ? { selectSetupId: response.dart_setup?.id } : {},
                )
            } catch (error) {
                console.error('Failed to create setup:', error)
                this.showNameModal = false
                if (error.response?.data?.errors) {
                    const errors = error.response.data.errors
                    const firstError = Object.values(errors)[0]
                    this.errorMessage = Array.isArray(firstError) ? firstError[0] : firstError
                } else if (error.response?.data?.message) {
                    this.errorMessage = error.response.data.message
                } else {
                    this.errorMessage = this.$t('setup_create.create_error')
                }
            } finally {
                this.isSaving = false
            }
        },
        removeComponent(component) {
            this.dartSetupStore.removeComponent(component)
        },
        getImageUrl(imageUrl) {
            if (!imageUrl) return ''
            if (imageUrl.startsWith('http')) return imageUrl
            const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || ''
            const baseUrl = apiBaseUrl.replace(/\/api\/?$/, '')
            return `${baseUrl}/${imageUrl}`
        },
        goBack() {
            if (this.hasStartedCustomizing) {
                this.showUnsavedChangesModal = true
            } else {
                this.returnToEntryPoint()
            }
        },
        confirmLeaveWithoutSaving() {
            this.showUnsavedChangesModal = false
            this.dartSetupStore.clearDraft()
            this.returnToEntryPoint()
        },
        // Reads the entry point set by whoever navigated into this flow (see
        // SetupsSelector/SetupsView) and clears it - it lives in the store so it
        // survives any number of round trips through setup-select-product. Uses
        // replace (not push) so the whole detour collapses back into a single
        // history slot - a caller relying on router.back() (e.g. MatchSetupView)
        // still lands on whatever was there before, no matter how many hops
        // happened in between.
        returnToEntryPoint(extraQuery = {}) {
            const entryPoint = this.dartSetupStore.entryPoint
            this.dartSetupStore.clearEntryPoint()

            if (entryPoint?.type === 'invite') {
                this.matchInviteStore.showInviteModal()
            } else if (entryPoint?.type === 'route') {
                this.$router.replace({
                    name: entryPoint.name,
                    params: entryPoint.params || {},
                    query: extraQuery,
                })
            } else {
                this.$router.replace({ name: 'setups' })
            }
        },
    },
}
</script>
